/**
 * THE ORDER AN ARTICLE IS SAVED IN — the article editor's (WikiEditor) save,
 * as a sequence of three writes, kept apart from the component so the order
 * can be held by a test (the web package has no DOM test environment).
 *
 * Library LB6-T8, review R1 / BF-02. Content and visibility are two writes:
 * the article (POST or PUT /wiki/articles) and its departments
 * (PUT /library/acl/documents/:id/departments). Whichever goes first is live
 * while the other is still in flight — or for good, when the second fails.
 * So the order is the one that FAILS CLOSED:
 *
 *   AN ARTICLE THAT EXISTS: its departments first, then the article, then any
 *   publish. A refused departments write stops the save before the content is
 *   written — new content is never live under the old, wider scope.
 *
 *   A NEW ARTICLE: it has no id until its create answers, so the create comes
 *   first — as a DRAFT (the editor never creates a published article; a
 *   publish is its own request, last) — then its departments, then any
 *   publish. A refused departments write keeps the saved draft unpublished.
 *
 * TAGS (decision 60, LB6c-T1) are a fourth write, through the one tag door
 * every document shares (PUT /knowledge/documents/:id/tags) — never inside the
 * article's body. They go after the article (a new one has no id before) and
 * BEFORE any publish: an existing article — departments, article, tags,
 * publish; a new one — article (draft), departments, tags, publish. A refused
 * tags write stops the save before the publish and reports the saved article.
 * No tag change, no tags write (`saveTags` null).
 */

export type ArticleSaveStep = 'departments' | 'article' | 'tags' | 'publish';

export type ArticleSaveOutcome<Saved extends { id: string }> =
  | {
      ok: true;
      saved: Saved;
      /** Whether a tags write was made (false when the set did not change). */
      tagsWritten: boolean;
    }
  | {
      ok: false;
      /** The write that failed; every write after it was not made. */
      failed: ArticleSaveStep;
      /** The article as written, when its write succeeded before the failure. */
      saved: Saved | null;
      /** Whether the departments were written before the failure. */
      departmentsWritten: boolean;
      /**
       * Whether the article's tags were written before the failure. False for
       * every failure at or before the tags step — a new article whose
       * departments were refused never reached it (LB6cT1-REV-01).
       */
      tagsWritten: boolean;
      error: unknown;
    };

export async function runArticleSave<Saved extends { id: string }>(io: {
  /** The article's id, or null for an article not yet created. */
  existingId: string | null;
  /** POST (new) or PUT (existing) the article; answers what was saved. */
  writeArticle: () => Promise<Saved>;
  /** Write the departments edit, if there is one, to the item with this id. */
  saveDepartments: (id: string) => Promise<void>;
  /** Write the article's tag set (PUT /knowledge/documents/:id/tags); null when the set did not change. */
  saveTags: ((id: string) => Promise<void>) | null;
  /** Publish (POST /wiki/articles/:id/publish); null when the save does not publish. */
  publish: ((id: string) => Promise<void>) | null;
}): Promise<ArticleSaveOutcome<Saved>> {
  let departmentsWritten = false;
  if (io.existingId !== null) {
    try {
      await io.saveDepartments(io.existingId);
      departmentsWritten = true;
    } catch (error) {
      return { ok: false, failed: 'departments', saved: null, departmentsWritten, tagsWritten: false, error };
    }
  }
  let saved: Saved;
  try {
    saved = await io.writeArticle();
  } catch (error) {
    return { ok: false, failed: 'article', saved: null, departmentsWritten, tagsWritten: false, error };
  }
  if (io.existingId === null) {
    try {
      await io.saveDepartments(saved.id);
      departmentsWritten = true;
    } catch (error) {
      return { ok: false, failed: 'departments', saved, departmentsWritten, tagsWritten: false, error };
    }
  }
  let tagsWritten = false;
  if (io.saveTags) {
    try {
      await io.saveTags(saved.id);
      tagsWritten = true;
    } catch (error) {
      return { ok: false, failed: 'tags', saved, departmentsWritten, tagsWritten, error };
    }
  }
  if (io.publish) {
    try {
      await io.publish(saved.id);
    } catch (error) {
      return { ok: false, failed: 'publish', saved, departmentsWritten, tagsWritten, error };
    }
  }
  return { ok: true, saved, tagsWritten };
}

/**
 * LB6-T8 review BF-01. The editor stays mounted when another item is opened in
 * it (the folder menu's "Add page", another page in the sidebar), and the
 * departments edit lives in the draft hook — so a change of item must drop it,
 * or the next item opens with the last one's unsaved departments and its first
 * save writes them. The one change that keeps it is the editor's OWN handover:
 * a new article's draft id replaced by the id its create answered (`handedOverId`,
 * the id the save just handed to the page), when a refused departments write
 * left the edit to be retried.
 */
export function keepsDepartmentsEdit(nextDocId: string, handedOverId: string | null): boolean {
  return handedOverId !== null && nextDocId === handedOverId;
}
